import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pin, Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import AdminCard from "./AdminCard";

export default function KomunikatyAdmin() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ title: "", content: "", importance: "normal", pinned: false });
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);

    const resetForm = () => {
        setForm({ title: "", content: "", importance: "normal", pinned: false });
        setEditingId(null);
    };

    const load = () => api.get("/announcements").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editingId) {
                await api.put(`/announcements/${editingId}`, form);
                toast.success("Komunikat zaktualizowany.");
            } else {
                await api.post("/announcements", form);
                toast.success("Komunikat dodany.");
            }
            resetForm();
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const startEdit = (it) => {
        setForm({ title: it.title, content: it.content, importance: it.importance || "normal", pinned: !!it.pinned });
        setEditingId(it.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const del = async (id) => {
        if (!window.confirm("Usunąć komunikat?")) return;
        await api.delete(`/announcements/${id}`);
        if (editingId === id) resetForm();
        await load();
        toast.success("Usunięto.");
    };

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-5 flex items-center gap-2">
                    {editingId ? <><Pin size={18} className="text-[#D4A537]" /> Edycja komunikatu</> : <><Plus size={18} /> Nowy komunikat</>}
                </h3>
                <form onSubmit={submit} className="space-y-4" data-testid="admin-komunikat-form">
                    <div className="space-y-2">
                        <Label>Tytuł *</Label>
                        <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-komunikat-title" />
                    </div>
                    <div className="space-y-2">
                        <Label>Treść *</Label>
                        <RichTextEditor
                            value={form.content}
                            onChange={(v) => setForm({ ...form, content: v })}
                            placeholder="Treść komunikatu (formatowanie, listy, linki)..."
                            testid="admin-komunikat-content"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Ważność</Label>
                            <Select value={form.importance} onValueChange={(v) => setForm({ ...form, importance: v })}>
                                <SelectTrigger data-testid="admin-komunikat-importance"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="normal">Informacja</SelectItem>
                                    <SelectItem value="important">Ważne</SelectItem>
                                    <SelectItem value="urgent">Pilne</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Przypięte</Label>
                            <div className="flex items-center gap-3 h-10">
                                <Switch checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} data-testid="admin-komunikat-pinned" />
                                <span className="text-sm text-gray-600">{form.pinned ? "Tak" : "Nie"}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button type="submit" disabled={loading} data-testid="admin-komunikat-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                            {loading ? "Zapisywanie..." : (editingId ? "Zapisz zmiany" : "Dodaj komunikat")}
                        </Button>
                        {editingId && (
                            <Button type="button" variant="outline" onClick={resetForm} data-testid="admin-komunikat-cancel" className="rounded-full">
                                Anuluj
                            </Button>
                        )}
                    </div>
                </form>
            </AdminCard>

            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-5">Lista komunikatów ({items.length})</h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {items.length === 0 && <p className="text-gray-500">Brak komunikatów.</p>}
                    {items.map((it) => (
                        <div key={it.id} className={`p-4 rounded-2xl bg-[#FAF8F3] border ${editingId === it.id ? "border-[#D4A537]" : "border-gray-100"}`} data-testid={`admin-komunikat-item-${it.id}`}>
                            <div className="flex justify-between items-start gap-3 mb-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    {it.pinned && <Pin size={12} className="text-[#D4A537]" />}
                                    <Badge variant="secondary" className="text-[10px] uppercase">{it.importance}</Badge>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => startEdit(it)} className="text-[#0D4F3C] hover:text-[#092E22] p-1.5 rounded-lg hover:bg-white" data-testid={`admin-komunikat-edit-${it.id}`} title="Edytuj">
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => del(it.id)} className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-white" data-testid={`admin-komunikat-delete-${it.id}`} title="Usuń">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                            <p className="font-serif text-base text-[#092E22]">{it.title}</p>
                            <div className="text-xs text-gray-500 line-clamp-2 rich-content" dangerouslySetInnerHTML={{ __html: it.content || "" }} />
                        </div>
                    ))}
                </div>
            </AdminCard>
        </div>
    );
}
